type CleanupFn = () => void;
const noop = (): void => {};

class CleanupManager {
    private cleanups: CleanupFn[] = [];
    private children = new Set<CleanupManager>();
    private parent: CleanupManager | null = null;
    private active = true;

    add(cleanup: CleanupFn): CleanupFn {
        if (!this.active) {
            cleanup();
            return noop;
        }
        this.cleanups.push(cleanup);
        return () => {
            const index = this.cleanups.indexOf(cleanup);
            if (index >= 0) this.cleanups.splice(index, 1);
        };
    }

    addChild(child: CleanupManager): CleanupFn {
        if (!child.active) return noop;
        if (!this.active) {
            child.run();
            return noop;
        }
        child.detach();
        this.children.add(child);
        child.parent = this;
        return () => {
            if (child.parent !== this) return;
            this.children.delete(child);
            child.parent = null;
        };
    }

    run(): void {
        if (!this.active) return;
        this.active = false;
        this.detach();

        const children = [...this.children];
        const cleanups = this.cleanups.splice(0).reverse();
        this.children.clear();
        const errors: unknown[] = [];

        children.forEach((child) => {
            try { child.run(); } catch (error) { errors.push(error); }
        });
        cleanups.forEach((cleanup) => {
            try { cleanup(); } catch (error) { errors.push(error); }
        });

        if (errors.length === 1) throw errors[0];
        if (errors.length > 1) throw new AggregateError(errors, 'Cleanup failed.');
    }

    private detach(): void {
        this.parent?.children.delete(this);
        this.parent = null;
    }
}

export {CleanupManager};
export type {CleanupFn};
